<template>
  <BaseButton
    v-if="shouldShowButton"
    class="no-print"
    small
    color="cyan white--text"
    :loading="isExporting"
    :disabled="isExporting || !hasRecords"
    @click="exportMurk31"
  >
    Export MURK 31
  </BaseButton>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { AssetTypes, LaborerTypes } from '@constants/knack'
import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'
import _times from 'lodash/times'

const WEBMERGE_TEMPLATE_URI = '1243556/cf6n15'

export default {
  name: 'MurkExportButton',
  props: {
    report: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isExporting: false,
    }
  },
  computed: {
    ...mapGetters([
      'getReportAssets',
      'getLocation',
      'getTheReport',
      'getTheReportForemanId',
      'isCivilJob',
      'isMurk31ExportEnabled',
      'isProjMgr',
      'isForeman',
    ]),

    shouldUseJobInventory() {
      return this.getTheReport?.JOB_ALLOWS_INVENTORY_SWAP === 1
    },

    shouldShowButton() {
      const hasPermission = this.isProjMgr || this.isForeman
      return this.isCivilJob && this.isMurk31ExportEnabled && hasPermission
    },

    laborAssignments() {
      const foremanId = this.getTheReportForemanId
      return _sortBy(
        this.getReportAssets({
          assetType: AssetTypes.LABORER,
          reportId: this.report.ID,
        }),
        [
          (l) => (_get(l, 'LABORER[0].id') === foremanId ? 0 : 1),
          (l) => (l.LABORERTYPE === LaborerTypes.FOREMAN ? 0 : 1),
          (l) => {
            let parts = l.LABORER_NAME.split(' ')
            return parts[parts.length - 1]
          },
        ]
      )
    },

    materialUsages() {
      return this.getReportAssets({
        assetType: AssetTypes.MATERIALS,
        reportId: this.report.ID,
      })
        .filter((m) => parseFloat(m.QUANTITY_USED) > 0)
        .filter(
          (m) =>
            _get(m, 'JOB_INVENTORY_ITEM[0].EXCLUDE_FROM_BILLING') !== 'True'
        )
    },

    /**
     * Export rows keyed by material description + unit: usages sharing both
     * merge into a single line with their quantities summed, so duplicate
     * entries of the same material part collapse to one line item.
     */
    groupedMaterialRows() {
      const groups = new Map()
      this.materialUsages.forEach((usage) => {
        const description = this.getMaterialDescription(usage)
        const unit = usage.UNIT_TYPE || ''
        // An unnamed usage stays its own line rather than merging with other unnamed ones
        const key = description ? `${description}|${unit}` : `__unnamed_${usage.ID}`
        const quantity = parseFloat(usage.QUANTITY_USED) || 0
        if (groups.has(key)) {
          groups.get(key).material_quantity += quantity
        } else {
          groups.set(key, {
            material_description: description,
            material_unit: unit,
            material_quantity: quantity,
          })
        }
      })
      // Round away float-summation artifacts (e.g. 1.1 + 2.2 = 3.3000000000000003)
      return Array.from(groups.values()).map((group) => ({
        ...group,
        material_quantity: Math.round(group.material_quantity * 1000) / 1000,
      }))
    },

    equipmentUsages() {
      return this.getReportAssets({
        assetType: AssetTypes.EQUIPMENT,
        reportId: this.report.ID,
      })
        .filter((e) => parseInt(e.EQUIPMENT_IS_EXCLUDED_FROM_BILLING) !== 1)
        .filter((e) => !e.NON_BILLABLE)
    },

    hasRecords() {
      return (
        this.laborAssignments.length > 0 ||
        this.materialUsages.length > 0 ||
        this.equipmentUsages.length > 0
      )
    },
  },
  methods: {
    /**
     * Convert "First Last" to "Last, First" format
     */
    convertToLastFirstName(fullName) {
      if (!fullName) return ''
      const nameParts = fullName.trim().split(/\s+/)
      if (nameParts.length >= 2) {
        const firstName = nameParts[0]
        const lastName = nameParts.slice(1).join(' ')
        return `${lastName}, ${firstName}`
      }
      return fullName
    },

    /**
     * Get empty row template with all fields
     */
    getEmptyRow() {
      return {
        // Labor fields
        laborer_name: '',
        laborer_trade_and_group: '',
        laborer_hours_regular: '',
        laborer_hours_premium: '',
        laborer_hours_total: '',
        // Material fields
        material_description: '',
        material_unit: '',
        material_quantity: '',
        // Equipment fields
        equipment_id: '',
        equipment_description: '',
        equipment_hours_regular: '',
        equipment_hours_standby: '',
      }
    },

    /**
     * Format labor assignment fields for a row
     */
    formatLaborFields(assignment) {
      if (!assignment) return {}
      return {
        laborer_name: this.convertToLastFirstName(assignment.LABORER_NAME),
        laborer_trade_and_group: _get(
          assignment,
          'LABOR_RATE[0].identifier',
          ''
        ),
        laborer_hours_regular: parseFloat(assignment.REGULAR_HOURS) || 0,
        laborer_hours_premium: parseFloat(assignment.OT) || 0,
        laborer_hours_total: parseFloat(assignment.TOTAL_HOURS) || 0,
      }
    },

    /**
     * Resolve the label for a material usage. Jobs with inventory swap
     * enabled label by the selected job inventory item; all other jobs
     * label by the material part name.
     */
    getMaterialDescription(usage) {
      const partName =
        _get(usage, 'MATERIALPART[0].identifier') || usage.MATERIAL_NAME || ''
      const description = this.shouldUseJobInventory
        ? _get(usage, 'JOB_INVENTORY_ITEM[0].identifier') || partName
        : partName
      return this.stripJobNumberSuffix(description)
    },

    /**
     * Format material usage fields for a row
     */
    formatMaterialFields(group) {
      if (!group) return {}
      return group
    },

    /**
     * Format equipment usage fields for a row
     */
    formatEquipmentFields(usage) {
      if (!usage) return {}

      return {
        equipment_id: usage.EQUIPMENT_ASSET_ || '',
        equipment_description: usage.EQUIPMENT_NAME || '',
        equipment_hours_regular: parseFloat(usage.REGULAR_HOURS_FOR_MURK) || 0,
        equipment_hours_standby: parseFloat(usage.NUMBER_STANDBY_HOURS) || 0,
      }
    },

    /**
     * Strip job number suffix from material names, e.g. "Some Material (225025)" → "Some Material"
     */
    stripJobNumberSuffix(name) {
      return name.replace(/\s*\(\d+\)\s*$/, '')
    },

    /**
     * Create unified rows by "zipping" labor, materials, and equipment together.
     * Each row contains fields for all three asset types at the same index.
     * Row count = max(labor.length, materials.length, equipment.length)
     */
    createUnifiedRows(labor, materials, equipment) {
      const maxLength = Math.max(
        labor.length,
        materials.length,
        equipment.length
      )

      if (maxLength === 0) {
        return []
      }

      return _times(maxLength, (index) => ({
        ...this.getEmptyRow(),
        ...this.formatLaborFields(labor[index]),
        ...this.formatMaterialFields(materials[index]),
        ...this.formatEquipmentFields(equipment[index]),
      }))
    },

    /**
     * Build the full payload for document generation
     */
    buildPayload() {
      const rows = this.createUnifiedRows(
        this.laborAssignments,
        this.groupedMaterialRows,
        this.equipmentUsages
      )

      return {
        report_date: _get(this.report, 'REPORT_DATE.date_formatted', ''),
        murk_31_work_description: _get(
          this.report,
          'MURK_31_WORK_DESCRIPTION',
          ''
        ),
        description_of_work_completed_today: _get(
          this.report,
          'DESCRIPTION_OF_WORK_COMPLETED_TODAY',
          ''
        ),
        job_contract_number: _get(this.getLocation, 'JOB_CONTRACT_NUMBER', ''),
        location_identifier: _get(this.getLocation, 'IDENTIFIER', ''),
        rows,
      }
    },

    /**
     * Send payload to WebMerge and handle download
     */
    async sendToWebMerge(payload) {
      const url = `https://www.webmerge.me/merge/${WEBMERGE_TEMPLATE_URI}?download=url`
      const { data } = await axios.post(url, payload)
      if (data.url) {
        window.location = data.url
      } else {
        throw new Error('No download URL returned from WebMerge')
      }
    },

    /**
     * Main export handler
     */
    async exportMurk31() {
      if (this.isExporting) return

      this.isExporting = true
      try {
        const payload = this.buildPayload()
        await this.sendToWebMerge(payload)
      } catch (error) {
        console.error('MURK 31 Export Error:', error)
        alert(
          'There was an error exporting the MURK 31 document. Please try again.'
        )
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>
